import { useState } from "react"

import { Packer } from "docx"
import { saveAs } from "file-saver"
import { ChevronDown, DownloadCloud, FileText, Loader2, Share2, Save } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { textToFile, generateMarkdownFromMapping, markdownToDocx } from "@/lib/file"
import { useUserSession } from "@session/UserSession"

interface DocumentCompareDropdownProps {
    runId: string | undefined
    content: string | object
    filename: string
    mapping: Record<string, string>
}
export function DocumentCompareDropdown({ runId, content, filename, mapping }: Readonly<DocumentCompareDropdownProps>) {
    const { client } = useUserSession()

    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [downloading, setDownloading] = useState<boolean>(false)
    const [saving, setSaving] = useState<boolean>(false)

    const handleExportAsDocx = async () => {
        setDownloading(true)
        const value = typeof content === "object" ? JSON.stringify(content, null, 2) : String(content)
        const markdown = generateMarkdownFromMapping(JSON.parse(value), mapping)

        const doc = markdownToDocx(markdown)
        const blob = await Packer.toBlob(doc)
        saveAs(blob, `${filename}.docx`)
        setDownloading(false)
    }

    const handleExportAsTxt = () => {
        const file = textToFile(content, filename)

        const url = window.URL.createObjectURL(file)
        const a = document.createElement("a")
        a.download = file.name
        a.href = url
        a.click()
        window.URL.revokeObjectURL(url)
    }

    const handleUpload = () => {
        setSaving(true)

        const file = textToFile(content, filename)
        const payload = {
            type: "6937db6e88a097243c6f867c",
            content: file
        }

        client.objects.create(payload)
            .then((response) => {
                toast.success(`Document ${response.id}`, {
                    description: "Document uploaded successfully",
                    duration: 2000
                })
            })
            .catch((error) => {
                toast.error("Document Upload failed", {
                    description: error.message,
                    duration: 2000
                })
            })
            .finally(() => {
                setSaving(false)
            })
    }

    const copyToClipboard = () => {
        if (!runId) {
            return
        }

        const compareUrl = window.location.href.includes(`/${runId}`) ? window.location.href : `${window.location.href}/${runId}`
        navigator.clipboard.writeText(compareUrl)
            .then(() => {
                toast.success("Compare Document link copied to clipboard!", {
                    duration: 2000
                })
            })
    }

    return (
        <>
            <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
                <DropdownMenuTrigger asChild disabled={downloading || saving}>
                    <Button>
                        {
                            (downloading) && (
                                <>
                                    <Loader2 className="animate-spin motion-safe:animate-[spin_3s_linear_infinite]" />
                                    <span>Donwloading...</span>
                                </>
                            )
                        }
                        {
                            (saving) && (
                                <>
                                    <Loader2 className="animate-spin motion-safe:animate-[spin_3s_linear_infinite]" />
                                    <span>Saving...</span>
                                </>
                            )
                        }
                        {
                            (!downloading && !saving) && (
                                <>
                                    <span>Actions</span>
                                    <ChevronDown className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`} />
                                </>
                            )
                        }
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-primary text-accent">
                    <DropdownMenuItem key={"export_docx"} onClick={handleExportAsDocx}>
                        <FileText />
                        Export as Docx
                    </DropdownMenuItem>
                    <DropdownMenuItem key={"export_txt"} onClick={handleExportAsTxt}>
                        <DownloadCloud />
                        Download Result
                    </DropdownMenuItem>
                    <DropdownMenuItem key={"save"} onClick={handleUpload}>
                        <Save />
                        Save Result
                    </DropdownMenuItem>
                    <DropdownMenuItem key={"share"} onClick={copyToClipboard} disabled={!runId}>
                        <Share2 />
                        Share Result
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
        </>
    )
}